import { QueryClient, QueryKey, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { GetCustomerDocumentUrlsQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";

export const CUSTOMER_DOCUMENT_URL_TTL_MS = 60 * 60 * 1000; // 1 hour (align with backend DOWNLOAD_URL_EXPIRATION_SECONDS)

function seedPerIdCache(
  queryClient: QueryClient,
  items: { id: string; documentUrl: string }[],
  now: number,
) {
  // Ensure default cache policy for individual URL queries
  queryClient.setQueryDefaults(["customerDocUrl"], {
    staleTime: CUSTOMER_DOCUMENT_URL_TTL_MS,
    cacheTime: CUSTOMER_DOCUMENT_URL_TTL_MS,
  });

  for (const item of items) {
    queryClient.setQueryData<string>(["customerDocUrl", item.id], item.documentUrl, {
      updatedAt: now,
    });
  }
}

export const CUSTOMER_DOCUMENTS_URLS = graphql(`
  query getCustomerDocumentUrls($ids: [String!]!) {
    getCustomerDocumentUrls(ids: $ids) {
      items {
        id
        documentUrl
      }
    }
  }
`);

export function useCustomerDocumentUrls(
  ids: string[] | undefined,
  options?: UseQueryOptions<
    GetCustomerDocumentUrlsQuery,
    unknown,
    GetCustomerDocumentUrlsQuery,
    QueryKey
  >,
) {
  const queryClient = useQueryClient();
  const { data, error, ...rest } = useGraphQlQuery(
    ["customerDocUrls", ids ?? []],
    CUSTOMER_DOCUMENTS_URLS,
    [{ ids: ids ?? [] }],
    {
      enabled: !!ids?.length && (options?.enabled ?? true),
      staleTime: CUSTOMER_DOCUMENT_URL_TTL_MS,
      cacheTime: CUSTOMER_DOCUMENT_URL_TTL_MS,
      onSuccess: (resp) => {
        // Seed per-id cache entries so downloads / other features can reuse without refetch
        seedPerIdCache(queryClient, resp.getCustomerDocumentUrls.items, Date.now());
        options?.onSuccess?.(resp);
      },
      ...options,
    },
  );

  return { data: data?.getCustomerDocumentUrls.items, error, ...rest };
}
